import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo, useRef } from 'react';
import { useMeasure } from 'react-use';
import { useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { ContentId } from 'src/utils/constants/contentId';
import { PresetDataPoint } from 'src/utils/strategyCalculator';

import {
  CHART_BORDER_COLOR,
  CHART_HEIGHT,
  CONTROL_POINT_COUNT,
  CURVE_COLOR,
  pickControlPoints,
  toChartData,
} from '../chartUtils';
import * as styles from './SellThroughGraph.css';

export interface SellThroughGraphProps {
  /** The preset curve data points (cumulative sales proportion 0→1) */
  data: PresetDataPoint[];
  /** The event date (used to compute the X axis) */
  eventDate: Date;
  /** Number of days remaining to event */
  daysToEvent: number;
}

export const SellThroughGraph = ({
  data,
  eventDate,
  daysToEvent,
}: SellThroughGraphProps) => {
  const [containerRef, { width: containerWidth }] =
    useMeasure<HTMLDivElement>();
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Resolve chart labels from ContentIds
  const sellThroughLabel = useContent(ContentId.StrategyCalculatorSellThrough);
  const dataPointsLabel = useContent(ContentId.StrategyCalculatorDataPoints);
  const eventLabel = useContent(ContentId.StrategyCalculatorEventLabel);

  const today = useMemo(() => {
    const d = new Date(eventDate);
    d.setDate(d.getDate() - daysToEvent);
    return d;
  }, [eventDate, daysToEvent]);

  const splineData = useMemo(
    () => toChartData(data, daysToEvent, eventDate),
    [data, daysToEvent, eventDate]
  );

  // X-axis range: today → event date (pad max slightly so plotLine isn't clipped)
  const xAxisRange = useMemo(
    () => ({
      min: today.getTime(),
      max: eventDate.getTime() + 12 * 60 * 60 * 1000, // +12 h so plotLine at event date is visible
    }),
    [today, eventDate]
  );

  // ~8 evenly spaced control point dots (interpolated along the spline)
  const controlPointData = useMemo(
    () =>
      pickControlPoints(
        splineData,
        today.getTime(),
        eventDate.getTime(),
        CONTROL_POINT_COUNT
      ),
    [splineData, today, eventDate]
  );

  // Use control point X positions as tick positions so labels align with dots
  const tickPositions = useMemo(() => {
    const positions = controlPointData.map(([ts]) => ts);
    // Ensure the event date is always the last tick (for the "Event" label)
    const eventTs = eventDate.getTime();
    if (positions.length === 0 || positions[positions.length - 1] !== eventTs) {
      positions.push(eventTs);
    }
    return positions;
  }, [controlPointData, eventDate]);

  const eventPlotLine = useMemo<Highcharts.XAxisPlotLinesOptions>(
    () => ({
      color: vars.color.textSecondary,
      width: 1,
      value: eventDate.getTime(),
      dashStyle: 'Dot',
      zIndex: 4,
    }),
    [eventDate]
  );

  const series = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const result: Highcharts.SeriesOptionsType[] = [];

    if (splineData.length > 0) {
      result.push({
        type: 'spline',
        name: sellThroughLabel,
        data: splineData,
        color: CURVE_COLOR,
        lineWidth: 2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 2,
      } as Highcharts.SeriesSplineOptions);
    }

    if (controlPointData.length > 0) {
      result.push({
        type: 'scatter',
        name: dataPointsLabel,
        data: controlPointData,
        color: CURVE_COLOR,
        marker: {
          radius: 4,
          lineColor: CURVE_COLOR,
          fillColor: CURVE_COLOR,
          lineWidth: 1,
          symbol: 'circle',
        },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 3,
      } as Highcharts.SeriesScatterOptions);
    }

    return result;
  }, [splineData, controlPointData, sellThroughLabel, dataPointsLabel]);

  const options: Highcharts.Options = useMemo(
    () => ({
      title: { text: '' },
      chart: {
        width: containerWidth > 0 ? containerWidth : undefined,
        height: CHART_HEIGHT,
        backgroundColor: 'transparent',
        spacing: [56, 12, 8, 12],
      },
      xAxis: {
        type: 'datetime',
        min: xAxisRange.min,
        max: xAxisRange.max,
        plotLines: [eventPlotLine],
        tickPositions,
        labels: {
          formatter: function (
            this: Highcharts.AxisLabelsFormatterContextObject
          ) {
            const ts = this.value as number;
            // Show "Event" for the last label at the event date
            if (ts >= eventDate.getTime()) return eventLabel;
            const d = new Date(ts);
            return `${d.getMonth() + 1}/${d.getDate()}`;
          },
          style: {
            color: vars.color.textSecondary,
            fontSize: vars.typography.fontSize.sm,
          },
        },
        lineWidth: 1,
        lineColor: CHART_BORDER_COLOR,
        tickLength: 3,
        tickWidth: 1,
        tickColor: vars.color.textSecondary,
        gridLineWidth: 0,
      },
      yAxis: {
        title: { text: undefined },
        labels: { enabled: false },
        lineWidth: 0,
        gridLineWidth: 0,
        tickLength: 0,
      },
      legend: { enabled: false },
      plotOptions: {
        spline: {
          lineWidth: 2,
          threshold: null,
        },
        series: {
          animation: { duration: 300 },
          stickyTracking: false,
        },
      },
      series,
      credits: { enabled: false },
      tooltip: { enabled: false },
    }),
    [
      containerWidth,
      xAxisRange,
      eventPlotLine,
      tickPositions,
      series,
      eventDate,
      eventLabel,
    ]
  );

  return (
    <div ref={containerRef} className={styles.chartContainer}>
      {containerWidth > 0 && (
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
        />
      )}
    </div>
  );
};
